import React, { useState } from 'react';
import Navigation from '../tulote/Navigation';
import Footer from '../tulote/Footer';
import ThemeTogglerTwo from '../tulote/ThemeTogglerTwo';
import { ThemeProvider } from '../ThemeProvider';
import {
  Plug,
  KeyRound,
  Send,
  Map,
  Copy,
  Check,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';

const DASHBOARD_URL = 'https://dashboard.lotes.info';
const API_BASE = 'https://api.lotespro.com';
const REDOC_URL = `${API_BASE}/api/v1/integrations/docs`;

// Bloque de código con botón para copiar
function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* noop */
    }
  };
  return (
    <div className="relative group">
      {language && (
        <span className="absolute top-3 left-4 text-xs font-mono text-gray-400">
          {language}
        </span>
      )}
      <button
        onClick={handleCopy}
        className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-xs text-gray-200 hover:bg-white/20 transition-colors"
        aria-label="Copiar código"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? 'Copiado' : 'Copiar'}
      </button>
      <pre className={`bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 ${language ? 'pt-9' : ''} rounded-lg overflow-x-auto text-sm`}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

const STEPS = [
  {
    icon: KeyRound,
    title: '1. Crea tu API key',
    text: 'En el Dashboard, ve a Integraciones y genera una API key. La copias una sola vez y la guardas en tu CRM.',
  },
  {
    icon: Send,
    title: '2. Tu CRM envía el estado',
    text: 'Cuando un lote cambia en tu CRM (se vende, se reserva), tu sistema llama a nuestro endpoint con la nueva info.',
  },
  {
    icon: Map,
    title: '3. El mapa se actualiza solo',
    text: 'El lote cambia de color en el mapa de inmediato. No tienes que tocar nada en LotesPro.',
  },
];

const BODY_FIELDS = [
  ['project_id', 'sí', 'ID del proyecto en LotesPro.'],
  ['lot_id', 'recomendado', 'ID interno del lote en LotesPro (lo sacas del catálogo). Único y estable.'],
  ['external_id', 'alternativa', 'El ID del lote en tu CRM, si lo mapeaste antes (ver sección de external_id).'],
  ['status', 'sí', 'Nuevo estado del lote (ver tabla de estados).'],
  ['reason', 'no', 'Motivo del cambio. Queda en el historial.'],
  ['notes', 'no', 'Notas extra. Quedan en el historial.'],
];

const STATUSES = [
  ['DISPONIBLE', 'Lote libre, a la venta.'],
  ['RESERVADO', 'Lote reservado por un cliente.'],
  ['EN_VENTA', 'En proceso de venta.'],
  ['VENDIDO', 'Venta cerrada.'],
  ['NO_DISPONIBLE', 'Fuera del mercado.'],
  ['DEVOLUCION', 'Devuelto, en revisión.'],
];

const ERRORS = [
  ['401', 'Falta el header X-API-Key, o la key es inválida o fue revocada.'],
  ['403', 'La key no tiene permiso para escribir estados de lotes.'],
  ['404', 'El proyecto o el lote no existe, o no pertenece a tu organización.'],
  ['422', 'Falta lot_id/external_id, o el estado no es válido.'],
];

const CATALOG_CURL = `curl "${API_BASE}/api/v1/integrations/lots?project_id=ab12cd34" \\
  -H "X-API-Key: lp_live_tu_api_key"`;

const CATALOG_RESPONSE = `{
  "project_id": "ab12cd34",
  "total": 320,
  "limit": 1000,
  "offset": 0,
  "items": [
    { "lot_id": "f1a2b3c4", "lot_number": "2", "block": "Mz.111",
      "stage": null, "external_id": null, "status": "DISPONIBLE" },
    { "lot_id": "a9b8c7d6", "lot_number": "2", "block": "Mz.38",
      "stage": null, "external_id": null, "status": "RESERVADO" }
  ]
}`;

const CURL = `curl -X PATCH "${API_BASE}/api/v1/integrations/lots/status" \\
  -H "X-API-Key: lp_live_tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": "ab12cd34",
    "lot_id": "f1a2b3c4",
    "status": "VENDIDO",
    "reason": "Venta cerrada en CRM"
  }'`;

const JS = `await fetch("${API_BASE}/api/v1/integrations/lots/status", {
  method: "PATCH",
  headers: {
    "X-API-Key": process.env.LOTESPRO_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    project_id: "ab12cd34",
    lot_id: "f1a2b3c4",
    status: "VENDIDO",
  }),
});`;

const PY = `import requests

requests.patch(
    "${API_BASE}/api/v1/integrations/lots/status",
    headers={"X-API-Key": "lp_live_tu_api_key"},
    json={
        "project_id": "ab12cd34",
        "lot_id": "f1a2b3c4",
        "status": "VENDIDO",
    },
)`;

const RESPONSE = `{
  "lot_id": "f1a2b3c4",
  "external_id": null,
  "lot_number": "2",
  "project_id": "ab12cd34",
  "previous_status": "RESERVADO",
  "status": "VENDIDO",
  "changed": true
}`;

const MAP_CURL = `curl -X POST "${API_BASE}/api/v1/integrations/lots/external-ids" \\
  -H "X-API-Key: lp_live_tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": "ab12cd34",
    "mappings": [
      { "lot_id": "f1a2b3c4", "external_id": "CRM-00123" },
      { "lot_id": "a9b8c7d6", "external_id": "CRM-00124" }
    ]
  }'`;

const MAP_SCRIPT = `import requests

BASE = "${API_BASE}/api/v1/integrations"
HEADERS = {"X-API-Key": "lp_live_tu_api_key"}
PROJECT = "ab12cd34"

# 1. Bajar el catálogo de lotes
catalogo = requests.get(
    f"{BASE}/lots", params={"project_id": PROJECT}, headers=HEADERS
).json()["items"]

# 2. Cruzar con tu CRM (aquí por block + lot_number) y armar los mapeos
mis_lotes = { ("Mz.111", "2"): "CRM-00123" }  # (block, lot_number) -> tu id
mappings = []
for lote in catalogo:
    clave = (lote["block"], lote["lot_number"])
    if clave in mis_lotes:
        mappings.append({"lot_id": lote["lot_id"], "external_id": mis_lotes[clave]})

# 3. Guardar tus external_id en lotespro
r = requests.post(
    f"{BASE}/lots/external-ids",
    headers=HEADERS,
    json={"project_id": PROJECT, "mappings": mappings},
)
print(r.json())  # { updated, failed, results: [...] }`;

const UPDATE_BY_EXTERNAL = `// Después del mapeo, actualizas con TU propio id:
{ "project_id": "ab12cd34", "external_id": "CRM-00123", "status": "VENDIDO" }`;

function IntegracionCRMPage() {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
        <Navigation />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 mb-4">
              <Plug className="w-8 h-8 text-brand-600 dark:text-brand-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
              Conecta tu CRM con LotesPro
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              ¿Ya tienes tu propio CRM? Sincroniza el estado de tus lotes con una
              llamada. Cuando cambias un lote en tu sistema, el mapa de LotesPro
              se actualiza al instante.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`${DASHBOARD_URL}/organizacion/integraciones`}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-white font-medium hover:bg-brand-600 transition-colors"
              >
                <KeyRound className="w-4 h-4" />
                Crear mi API key
              </a>
              <a
                href={REDOC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-5 py-2.5 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Referencia técnica
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="/contacto"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-5 py-2.5 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Hablar con el equipo
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Cómo funciona */}
          <section className="mb-14">
            <SectionTitle>Cómo funciona</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-3">
              {STEPS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.title}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <Icon className="w-6 h-6 text-brand-500 mb-3" />
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {s.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{s.text}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Paso 1: API key */}
          <section className="mb-14">
            <SectionTitle>1. Consigue tu API key</SectionTitle>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Entra al Dashboard con tu cuenta de administrador, abre el menú de
              usuario y ve a <strong>Integraciones</strong>. Ahí creas una API key
              con un nombre (ej: “CRM Producción”). La key se muestra{' '}
              <strong>una sola vez</strong>: cópiala y guárdala en un lugar seguro.
              Si se filtra, la revocas y creas otra.
            </p>
            <a
              href={`${DASHBOARD_URL}/organizacion/integraciones`}
              className="inline-flex items-center gap-1.5 text-brand-600 dark:text-brand-400 font-medium hover:underline"
            >
              Ir a Integraciones en el Dashboard
              <ExternalLink className="w-4 h-4" />
            </a>
          </section>

          {/* Paso 2: catálogo */}
          <section className="mb-14">
            <SectionTitle>2. Descarga el catálogo de lotes</SectionTitle>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Antes de actualizar, haz un <strong>sync inicial</strong>: pide el
              catálogo del proyecto y guarda nuestro <code className="font-mono">lot_id</code>{' '}
              junto a cada lote de tu CRM. Así luego actualizas por{' '}
              <code className="font-mono">lot_id</code>, que es único y estable —
              nunca te confundes de manzana aunque el número de lote se repita.
            </p>
            <div className="mb-4">
              <CodeBlock language="bash" code={CATALOG_CURL} />
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-2 text-sm">
              Respuesta (paginada con <code className="font-mono">limit</code> y{' '}
              <code className="font-mono">offset</code>; <code className="font-mono">total</code>{' '}
              te dice cuántos lotes hay):
            </p>
            <CodeBlock language="json" code={CATALOG_RESPONSE} />
          </section>

          {/* Paso 3: endpoint */}
          <section className="mb-14">
            <SectionTitle>3. Actualiza el estado de un lote</SectionTitle>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Con el <code className="font-mono">lot_id</code> que guardaste, haz una
              petición con tu API key en el header{' '}
              <code className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded text-sm font-mono">
                X-API-Key
              </code>
              .
            </p>
            <div className="mb-6">
              <CodeBlock language="HTTP" code={`PATCH ${API_BASE}/api/v1/integrations/lots/status`} />
            </div>

            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Cuerpo de la petición (JSON)
            </h3>
            <div className="overflow-x-auto mb-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-4 font-medium">Campo</th>
                    <th className="py-2 pr-4 font-medium">Requerido</th>
                    <th className="py-2 pr-4 font-medium">Descripción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {BODY_FIELDS.map(([field, req, desc]) => (
                    <tr key={field} className="text-gray-700 dark:text-gray-300 align-top">
                      <td className="py-2.5 pr-4">
                        <code className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded text-xs font-mono font-bold">
                          {field}
                        </code>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{req}</td>
                      <td className="py-2.5 pr-4">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Identifica el lote con <code className="font-mono">lot_id</code>{' '}
              (recomendado) o <code className="font-mono">external_id</code>. Debes enviar uno
              de los dos.
            </p>
          </section>

          {/* Opcional: external_id */}
          <section className="mb-14">
            <SectionTitle>Opcional: usa tu propio ID (external_id)</SectionTitle>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Si prefieres trabajar con los IDs de tu CRM en vez de nuestro{' '}
              <code className="font-mono">lot_id</code>, puedes guardarlos en nuestros
              lotes una sola vez. Después actualizas el estado usando tu propio{' '}
              <code className="font-mono">external_id</code>. Cada{' '}
              <code className="font-mono">external_id</code> es único por proyecto.
            </p>
            <div className="mb-6">
              <CodeBlock language="HTTP" code={`POST ${API_BASE}/api/v1/integrations/lots/external-ids`} />
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Mandas pares <code className="font-mono">lot_id → external_id</code> en bloque
              (hasta 1000 por request). Cada item responde por separado: los que chocan
              se marcan, el resto se guarda igual.
            </p>
            <div className="mb-6">
              <CodeBlock language="bash" code={MAP_CURL} />
            </div>

            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              Script de ejemplo (Python)
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-2 text-sm">
              Baja el catálogo, lo cruza con tu CRM y guarda tus IDs:
            </p>
            <div className="mb-6">
              <CodeBlock language="python" code={MAP_SCRIPT} />
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-2 text-sm">
              Ya con el mapeo hecho, actualizas por tu propio ID:
            </p>
            <CodeBlock language="json" code={UPDATE_BY_EXTERNAL} />
          </section>

          {/* Estados */}
          <section className="mb-14">
            <SectionTitle>Estados válidos</SectionTitle>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {STATUSES.map(([code, desc]) => (
                    <tr key={code} className="text-gray-700 dark:text-gray-300">
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        <code className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 px-2 py-0.5 rounded text-xs font-mono font-bold">
                          {code}
                        </code>
                      </td>
                      <td className="py-2.5 pr-4">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Ejemplos */}
          <section className="mb-14">
            <SectionTitle>Ejemplos</SectionTitle>
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">cURL</h3>
                <CodeBlock language="bash" code={CURL} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">JavaScript (fetch)</h3>
                <CodeBlock language="javascript" code={JS} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Python (requests)</h3>
                <CodeBlock language="python" code={PY} />
              </div>
            </div>
          </section>

          {/* Respuesta */}
          <section className="mb-14">
            <SectionTitle>Respuesta</SectionTitle>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Si todo sale bien, recibes <code className="font-mono">200 OK</code> con
              el lote actualizado. El campo <code className="font-mono">changed</code> es{' '}
              <code className="font-mono">false</code> si el estado ya era el mismo.
            </p>
            <CodeBlock language="json" code={RESPONSE} />
          </section>

          {/* Errores */}
          <section className="mb-14">
            <SectionTitle>Errores comunes</SectionTitle>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {ERRORS.map(([code, desc]) => (
                    <tr key={code} className="text-gray-700 dark:text-gray-300 align-top">
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        <code className="bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 px-2 py-0.5 rounded text-xs font-mono font-bold">
                          {code}
                        </code>
                      </td>
                      <td className="py-2.5 pr-4">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* CTA final */}
          <section className="rounded-2xl bg-gradient-to-r from-brand-50 to-blue-50 dark:from-brand-900/20 dark:to-blue-900/20 border border-brand-200 dark:border-brand-800 p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              ¿Listo para conectar tu CRM?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-xl mx-auto">
              Crea tu API key y empieza a sincronizar. Si necesitas ayuda con la
              integración, escríbenos.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href={`${DASHBOARD_URL}/organizacion/integraciones`}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-white font-medium hover:bg-brand-600 transition-colors"
              >
                <KeyRound className="w-4 h-4" />
                Crear mi API key
              </a>
              <a
                href="/contacto"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-5 py-2.5 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Hablar con el equipo
              </a>
            </div>
          </section>
        </main>

        <Footer />

        <div className="fixed bottom-6 right-6 z-50 hidden sm:block">
          <ThemeTogglerTwo />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default IntegracionCRMPage;
